import { Link } from 'react-router-dom';

type Lp = (path: string) => string;

export function AboutBodyEn({ lp }: { lp: Lp }) {
  return (
    <article className="about-section">
      <h1>About This Free Lottery Number Generator</h1>
      <p>
        This free tool helps you instantly generate random number combinations for <strong>Powerball</strong>,{' '}
        <strong>Mega Millions</strong>, or <strong>Korean Lotto 6/45</strong>. It uses the official number ranges for each game and checks
        every set against thousands of real past jackpot draws.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>How Does a Lottery Random Number Generator Work?</h2>
      <p>
        Our generator uses <strong>pseudo-random number generation</strong> (PRNG) via JavaScript&apos;s built-in <code>Math.random()</code>{' '}
        function. For lottery number picking, PRNG is more than sufficient.{' '}
        <strong>No method of choosing lottery numbers gives you better odds</strong> than any other.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>What About &quot;Hot&quot; and &quot;Cold&quot; Lottery Numbers?</h2>
      <p>
        Past frequency does <strong>not predict future draws</strong>. This is the <em>Gambler&apos;s Fallacy</em>. Each drawing is an
        independent event.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Historical Jackpot Matching</h2>
      <p>
        Every set is compared against 4,000+ real past Powerball and Mega Millions winning draws.{' '}
        <Link to={lp('/simulate')}>Try the Jackpot Match Simulator</Link> to see what a match looks like.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>How Our Historical Jackpot Database Works</h2>
      <p>
        Our jackpot database is one of the most comprehensive freely available collections of lottery draw results. It contains over{' '}
        <strong>4,000 United States lottery draws</strong>, including Powerball results dating back to January 2010 and Mega Millions results
        dating back to 2002 when the game was still known as &quot;The Big Game.&quot; This extensive US dataset captures every major format
        change, including the Powerball matrix adjustment in October 2015 (when the main ball pool expanded from 59 to 69 and the Powerball
        pool shrank from 35 to 26) and the Mega Millions restructuring in October 2017 (when the main pool grew from 75 to 70 and the Mega Ball
        pool expanded from 15 to 25), plus the April 2025 Mega Millions redesign (Mega Ball pool reduced to 24).
      </p>
      <p>
        In addition to US draws, the database includes over <strong>1,200 Korean Lotto 6/45 draws</strong> sourced from official Korean lottery
        data. The Korean Lotto 6/45 game has been running since 2002 with a consistent format (six main numbers from 1 to 45 plus one bonus
        number), making it an excellent dataset for historical comparison.
      </p>
      <p>
        The US draw data is sourced primarily from <strong>data.ny.gov</strong>, the official open data portal of the State of New York,
        which publishes verified winning numbers for both Powerball and Mega Millions. Korean lottery data is sourced from the official Korean
        lottery website and verified government data feeds. Our automated pipeline fetches new results on a weekly basis, typically within 24
        hours of each draw taking place. The entire process is automated: a scheduled job runs every week, downloads any new draw results,
        validates them against expected formats and ranges, and adds them to the database. This ensures that our historical jackpot matching
        feature always uses the most up-to-date information available.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Supported Lottery Games</h2>
      <p>Our generator supports three official lottery game formats. Here is a detailed breakdown of each supported game:</p>
      <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
        <li>
          <strong>Powerball</strong> — the flagship American lottery game. Players select 5 main numbers from a pool of 1 to 69, plus 1
          Powerball number from a separate pool of 1 to 26. Drawings are held three times per week on Monday, Wednesday, and Saturday evenings.
          The jackpot starts at $20 million and rolls over until won. Overall odds of winning any prize are approximately 1 in 24.9, while the
          odds of matching all five main numbers plus the Powerball for the jackpot are approximately 1 in 292,201,338. Powerball is operated by
          the Multi-State Lottery Association (MUSL) and is available in 45 states, the District of Columbia, Puerto Rico, and the U.S. Virgin
          Islands.
        </li>
        <li>
          <strong>Mega Millions</strong> — another major American multi-state lottery game. Players choose 5 main numbers from 1 to 70, plus 1
          Mega Ball number from 1 to 24. Drawings take place on Tuesday and Friday evenings. The jackpot starts at $50 million and grows with
          each rollover. The odds of winning the Mega Millions jackpot are approximately 1 in 290,472,336. Mega Millions is offered in the same
          jurisdictions as Powerball and is administered by a consortium of state lotteries.
        </li>
        <li>
          <strong>Korean Lotto 6/45</strong> — South Korea&apos;s national lottery game, officially known as Lotto 6/45 and operated by the Korea
          Lottery Commission. Players select 6 numbers from a pool of 1 to 45. A bonus number is also drawn for second-prize determination.
          Drawings are held every Saturday. The odds of matching all six main numbers are approximately 1 in 8,145,060. The game is one of the
          most popular forms of legal lottery entertainment in South Korea and has been running continuously since its launch in December 2002.
        </li>
      </ul>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Our Commitment to Data Accuracy</h2>
      <p>
        Data accuracy is a cornerstone of our service. Every draw result in our database is sourced from verified government APIs and official
        lottery organization publications. For US draws, we rely on the State of New York&apos;s open data API at data.ny.gov, which is the same
        authoritative source used by news organizations, researchers, and lottery analytics firms. For Korean Lotto 6/45, we use official data
        published by the Korea Lottery Commission.
      </p>
      <p>
        Our automated weekly refresh process includes multiple validation checks to ensure data integrity. Each incoming draw result is verified
        against the expected number ranges for its game type — for example, a Powerball main number must fall between 1 and 69, and a Powerball
        bonus must fall between 1 and 26. Duplicate draw entries are detected and rejected automatically. The total count of main numbers per draw
        is validated to match the expected format (five for Powerball and Mega Millions, six for Korean Lotto 6/45). If any validation check
        fails, the affected record is flagged for manual review rather than being silently added to the database.
      </p>
      <p>
        We also perform periodic full-database audits where every record is re-verified against the source API to catch any discrepancies that may
        have been introduced by upstream data corrections or format changes. This multi-layered approach to data quality means that when your
        generated numbers are compared against historical jackpot draws, you can be confident that the comparison is based on accurate,
        verified winning numbers.
      </p>

      <hr style={{ border: 0, borderTop: '1px solid var(--input-border)', margin: '28px 0', opacity: 0.7 }} />

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Founder Story</h2>
      <p>
        Why lean into jackpot matching and &ldquo;synchronicity&rdquo; as an experience? The longer personal note is on{' '}
        <Link to={lp('/story')}>Story</Link>. Short Q&amp;A on the same themes is on <Link to={lp('/faq')}>FAQ</Link>.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Responsible Gaming Guide</h2>
      <p>Lottery games should be treated as <strong>entertainment, not an investment strategy</strong>.</p>
      <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
        <li>Set a budget — only spend what you can afford to lose.</li>
        <li>Powerball jackpot odds are about 1 in 292.2 million; Mega Millions about 1 in 290.5 million.</li>
        <li>
          Seek help if needed — <strong>1-800-522-4700</strong> (National Council on Problem Gambling).
        </li>
      </ul>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Who Built This Tool?</h2>
      <p>
        Built for entertainment. No strategy or prediction is involved. This site is not affiliated with any official lottery organization.
      </p>

      <div className="disclaimer">
        <strong>Disclaimer:</strong> For entertainment only. Not gambling advice. Check your local laws.
      </div>
    </article>
  );
}

export function AboutBodyKo({ lp }: { lp: Lp }) {
  return (
    <article className="about-section">
      <h1>이 도구에 대해</h1>
      <p>
        이 무료 로또 번호 생성기는 <strong>로또 6/45</strong> 번호를 난수로 생성합니다. 공식 번호 범위(1~45)를 사용하며, 생성된 번호는 역대 당첨
        번호와 자동으로 비교됩니다.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>난수 번호 생성 원리</h2>
      <p>본 도구는 JavaScript의 Math.random() 함수를 사용하여 의사난수를 생성합니다. 모든 조합은 동일한 확률로 선택됩니다.</p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>역대 당첨 번호 데이터베이스</h2>
      <p>
        본 사이트는 방대한 역대 당첨 번호 데이터베이스를 보유하고 있습니다. 한국 로또 6/45의 경우 2002년 12월 첫 추첨부터 현재까지 1,200회 이상의
        추첨 결과가 저장되어 있으며, 미국 복권의 경우 Powerball과 Mega Millions을 합쳐 4,000회 이상의 추첨 데이터를 보유하고 있습니다. 이 데이터는
        각국의 공식 복권 기관에서 발표한 공식 데이터를 기반으로 하며, 동행복권(한국) 및 미국 각 주 복권 위원회의 공식 발표 자료를 출처로 합니다.
        데이터베이스는 매주 자동으로 업데이트되어 최신 추첨 결과가 반영됩니다. 이용자가 생성한 번호는 이 데이터베이스와 자동 비교되어, 과거에 동일한
        번호 조합이 당첨된 적이 있는지, 몇 개의 번호가 일치하는지 등의 정보를 실시간으로 확인할 수 있습니다. 이 기능은 순전히 재미와 참고 목적이며,
        미래 당첨 확률과는 전혀 관련이 없습니다.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>지원하는 복권 게임</h2>
      <p>
        현재 본 사이트에서 지원하는 복권 게임은 다음과 같습니다. 첫째, <strong>로또 6/45</strong>는 한국의 대표적인 복권 게임으로, 1부터 45까지의
        숫자 중 6개를 선택하며, 보너스 번호 1개가 별도로 추첨됩니다. 추첨은 매주 토요일에 진행되며, 동행복권에서 공식 운영합니다. 둘째,{' '}
        <strong>Powerball</strong>은 미국의 대표적인 복권 게임으로, 1부터 69까지의 숫자 중 5개의 흰 공과 1부터 26까지의 숫자 중 1개의 파워볼을
        선택합니다. 셋째, <strong>Mega Millions</strong>는 또 다른 미국 복권 게임으로, 1부터 70까지의 숫자 중 5개의 흰 공과 1부터 24까지의 숫자 중
        1개의 메가볼을 선택합니다. 각 게임별 규칙과 당첨 등급 구조는 해당 공식 기관의 규정을 따릅니다.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>데이터 정확성</h2>
      <p>
        본 사이트의 당첨 번호 데이터는 정부 기관 및 공식 복권 운영 기관의 API와 공식 발표 자료를 통해 수집됩니다. 한국 로또 6/45 데이터는
        동행복권의 공식 결과를 기반으로 하며, 미국 Powerball 및 Mega Millions 데이터는 각 주 복권 위원회의 공식 데이터를 활용합니다. 데이터
        업데이트는 매주 자동화된 프로세스를 통해 이루어지며, 새로운 추첨 결과가 공식 발표된 후 가능한 빠른 시간 내에 반영됩니다. 자동화 시스템은
        데이터 무결성을 보장하기 위해 수집된 정보를 교차 검증하며, 오류가 감지될 경우 수동으로 검토하고 수정합니다. 다만, 데이터의 정확성을
        보장하기 위해 최선을 다하고 있으나, 기술적 오류 또는 출처 데이터의 지연으로 인해 일시적인 부정확성이 발생할 수 있습니다. 공식 당첨 번호는
        반드시 해당 복권 기관의 공식 웹사이트에서 확인하시기 바랍니다.
      </p>

      <hr style={{ border: 0, borderTop: '1px solid var(--input-border)', margin: '28px 0', opacity: 0.7 }} />

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>만든 사람의 이야기</h2>
      <p>
        역대 당첨 매칭과 &ldquo;공명&rdquo;을 왜 경험으로 풀었는지 긴 글은 <Link to={lp('/story')}>이야기</Link>에서, 같은 주제 질문·답변은{' '}
        <Link to={lp('/faq')}>FAQ</Link>에 모았습니다.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>책임감 있는 복권 구매</h2>
      <p>
        복권은 <strong>오락으로만</strong> 즐기세요. 도박 문제가 있으시면 <strong>1577-0024</strong>로 연락하세요.
      </p>

      <div className="disclaimer">
        <strong>면책 조항:</strong> 오락 목적으로만 제공됩니다. 동행복권과 제휴 관계가 없습니다.
      </div>
    </article>
  );
}
